// Lista de condominios de respaldo, desde el navegador, si `get_condominios` no está en el
// ambiente. Todo lo demás lo arma el servidor (`./api.ts`). El dataset que armaba aquí el portal
// entero, con ~80 peticiones en serie, se retiró al pasar el último menú a RPC (2026-10-16).

import { supabase } from "@/integrations/supabase/client";
import { fetchAllRows } from "@/utils/supabasePagination";
import type { CondominioRef } from "@/types/condominio";

const CONCEPTO_MANTENIMIENTO = 11;
const CHUNK = 150; // tamaño de lote para filtros .in() (evita URLs demasiado largas)

// Ejecuta un .in() por lotes y pagina cada lote para superar el límite de ~1000 filas.
async function fetchInChunks<T = any>(
  ids: Array<number | string>,
  build: (chunk: Array<number | string>, from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const rows = await fetchAllRows<T>((from, to) => build(chunk, from, to));
    out.push(...rows);
  }
  return out;
}

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

/**
 * Lista de condominios = proyectos que tienen cuentas de mantenimiento.
 * Recorre la cadena en sentido inverso (acuerdos -> cuenta -> padre -> propiedad -> proyecto).
 */
export async function fetchCondominios(): Promise<CondominioRef[]> {
  // 1. Cuentas con acuerdos de mantenimiento
  const acuerdos = await fetchAllRows<{ id_cuenta_cobranza: number }>((from, to) =>
    supabase
      .from("acuerdos_pago")
      .select("id_cuenta_cobranza")
      .eq("id_concepto", CONCEPTO_MANTENIMIENTO)
      .range(from, to),
  );
  const mantCcIds = uniq(acuerdos.map((a) => a.id_cuenta_cobranza).filter(Boolean));
  if (mantCcIds.length === 0) return [];

  // 2. cuenta mantenimiento -> padre
  const mantCuentas = await fetchInChunks<{ id: number; id_cuenta_cobranza_padre: number | null }>(
    mantCcIds,
    (chunk, from, to) =>
      supabase.from("cuentas_cobranza").select("id, id_cuenta_cobranza_padre").in("id", chunk as number[]).range(from, to),
  );
  const padreIds = uniq(mantCuentas.map((c) => c.id_cuenta_cobranza_padre).filter(Boolean) as number[]);
  if (padreIds.length === 0) return [];

  // 3. padre -> propiedad
  const padres = await fetchInChunks<{ id: number; id_propiedad: number | null }>(padreIds, (chunk, from, to) =>
    supabase.from("cuentas_cobranza").select("id, id_propiedad").in("id", chunk as number[]).range(from, to),
  );
  const propIds = uniq(padres.map((p) => p.id_propiedad).filter(Boolean) as number[]);
  if (propIds.length === 0) return [];

  // 4. propiedad -> edificio_modelo
  const props = await fetchInChunks<{ id: number; id_edificio_modelo: number | null }>(propIds, (chunk, from, to) =>
    supabase.from("propiedades").select("id, id_edificio_modelo").in("id", chunk as number[]).range(from, to),
  );
  const emIds = uniq(props.map((p) => p.id_edificio_modelo).filter(Boolean) as number[]);

  // 5. edificio_modelo -> edificio
  const ems = await fetchInChunks<{ id: number; id_edificio: number | null }>(emIds, (chunk, from, to) =>
    supabase.from("edificios_modelos").select("id, id_edificio").in("id", chunk as number[]).range(from, to),
  );
  const edIds = uniq(ems.map((e) => e.id_edificio).filter(Boolean) as number[]);

  // 6. edificio -> proyecto
  const eds = await fetchInChunks<{ id: number; id_proyecto: number | null }>(edIds, (chunk, from, to) =>
    supabase.from("edificios").select("id, id_proyecto").in("id", chunk as number[]).range(from, to),
  );
  const proyectoIds = uniq(eds.map((e) => e.id_proyecto).filter(Boolean) as number[]);
  if (proyectoIds.length === 0) return [];

  // 7. proyectos
  const proyectos = await fetchInChunks<{ id: number; nombre: string | null }>(proyectoIds, (chunk, from, to) =>
    supabase.from("proyectos").select("id, nombre").in("id", chunk as number[]).eq("activo", true).range(from, to),
  );
  return proyectos
    .map((p) => ({ id: p.id, nombre: p.nombre ?? `Proyecto ${p.id}` }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}
